
import React from "react";
import { Project, RibaStage } from "@/types";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface StageGanttBarProps {
  stage: RibaStage;
  project: Project;
  startDate: Date;
  endDate: Date;
  timelineScale: number;
}

export const StageGanttBar: React.FC<StageGanttBarProps> = ({
  stage,
  project,
  startDate,
  endDate,
  timelineScale
}) => {
  const isMobile = useIsMobile();
  
  // Determine color based on stage status
  const bgColor = 
    stage.status === "completed" ? "bg-green-500" : 
    stage.status === "in-progress" ? "bg-blue-500" : 
    "bg-gray-300";
  
  // Calculate width based on stage weight in project timeline
  const position = 5 + (stage.id - 1) * 12; // Fixed positions with 12% spacing
  const barWidth = 10; // Fixed width for all stage bars

  return (
    <div className="flex items-center h-10 mb-3">
      {/* Stage label container - responsive with no fixed width */}
      <div className="flex items-center pr-2 w-1/4 min-w-16 max-w-48">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-sm mr-2 flex-shrink-0">
          {stage.id}
        </span>
        {/* On mobile, only show stage number, not name */}
        {!isMobile && <span className="font-medium truncate">{stage.name}</span>}
      </div>
      
      {/* Timeline container */}
      <div className="flex-1 relative h-8">
        {/* The actual bar */}
        <div 
          className={cn(
            "absolute h-full rounded-md shadow-sm",
            bgColor
          )}
          style={{
            left: `${position}%`,
            width: `${barWidth}%`,
          }}
        >
          {/* Status text container */}
          <div className="h-full flex items-center justify-center overflow-hidden">
            <div className="text-xs font-medium text-white px-2 truncate">
              {stage.status === "completed" ? "100%" :
               stage.status === "in-progress" ? `${stage.progress}%` :
               ""}
            </div>
          </div>
        </div>
      </div>

      {/* Status display */}
      <div className="text-xs sm:text-sm text-right w-1/5 min-w-16 max-w-24 truncate">
        {stage.status === "completed" ? "Complete" : 
         stage.status === "in-progress" ? "In Progress" : 
         "Not Started"}
      </div>
    </div>
  );
};
